import type {ConsentState} from "./consentStore";

/**
 * When the always-available consent-withdrawal control is shown (kitecorp/transferify-ui#2675).
 * React-free by mandate (epic #26), so the rule can be asserted directly rather than through a
 * rendered shell.
 *
 * <p><b>Why the control exists.</b> Withdrawing consent has to be as easy as giving it (GDPR
 * Art. 7(3)), and the German DSK's Orientierungshilfe (Nov 2024) is specific about what fails that
 * test: a control the visitor can only reach by opening the privacy policy and scrolling to find it
 * is itself an obstacle, and a link to a provider's own opt-out page is not a withdrawal control at
 * all. Consent is given in one press on a bar that is on every page, so withdrawal is one press on
 * a control that is on every page. Until this ticket the only way back was the top bar's Resources
 * dropdown, which is two.
 *
 * <p><b>Why it is hidden before a decision.</b> The banner IS the control then, and it already
 * carries Reject and Customize. Two controls asking the same question at once would leave the
 * visitor to work out which one answers it, and the second would sit under a bar the page has
 * already reserved room for.
 *
 * <p><b>Every route, no exceptions.</b> There is deliberately no route rule here, unlike
 * `nav/siteFooterRoutes`. The delivery surfaces that carry no footer DO run the advertising tag and
 * DO show the consent bar, so they are exactly the pages where a visitor may want to take the
 * permission back. A route exemption here would be an exemption from Art. 7(3).
 */
export function showsWithdrawalControl(consent: ConsentState): boolean {
	return consent.decided;
}

/** The control's box in viewport pixels: the three edges a page that lays itself out around it can meet. */
export interface WithdrawalControlBox {
	top: number;
	right: number;
	bottom: number;
}

const BOX_PROPERTIES = {top: "--withdrawal-control-top", right: "--withdrawal-control-right", bottom: "--withdrawal-control-bottom"} as const;

/**
 * What the control publishes on the document root while it is on the page (kitecorp/transferify#536), the way the
 * consent banner publishes `--consent-bar-reserve`: the control is fixed in a corner, so a page that places its own
 * fixed content at the window's bottom reads where the control is rather than restating its CSS. Measured rather
 * than derived from the stylesheet, so whatever moves the control (its phone inset, a safe-area inset) moves this
 * with it.
 */
export function withdrawalControlProperties(box: WithdrawalControlBox): [string, string][] {
	return (Object.keys(BOX_PROPERTIES) as (keyof WithdrawalControlBox)[])
		.map(edge => [BOX_PROPERTIES[edge], `${box[edge]}px`]);
}

/** The property names alone, for the control to remove when it leaves the page. */
export const WITHDRAWAL_CONTROL_PROPERTIES: readonly string[] = Object.values(BOX_PROPERTIES);

/** The published box, or null while the control is not on the page; a partial one is no box at all. */
export function readWithdrawalControlBox(read: (property: string) => string): WithdrawalControlBox | null {
	const [top, right, bottom] = [BOX_PROPERTIES.top, BOX_PROPERTIES.right, BOX_PROPERTIES.bottom]
		.map(property => Number.parseFloat(read(property)));
	return [top, right, bottom].every(Number.isFinite) ? {top, right, bottom} : null;
}
